// Hintergrund-Dekor hinter dem App-Content. Ersetzt seit dem Klar-
// Redesign 2026-07 das Sternenhimmel-Canvas (star-field.ts): mountet
// einmalig eine feste DOM-Struktur (#bg-layers, Styles in app.css),
// der aktive Stil wird rein per CSS ueber html[data-bg-theme]
// selektiert (gesetzt von lib/bg-settings.ts). Kein Canvas, kein
// Render-Loop -- alle Dynamik sind langsame CSS-Animationen.
//
// Einzige JS-Laufzeit-Arbeit: das Spotlight des Stils "raster" folgt
// der Maus (gedaempft) bzw. driftet idle langsam sinusfoermig ueber
// die CSS-Variablen --glow-x/--glow-y. Der rAF-Loop laeuft NUR wenn
// der Raster-Stil aktiv, der Tab sichtbar und reduced-motion aus ist
// -- die Safari-Energie-Lektion vom alten Starfield gilt weiter.

import { getBgSettings, subscribeBgSettings } from "./bg-settings";

let started = false;

export function startBgLayers(): void {
  if (started) return;
  started = true;

  const host = document.createElement("div");
  host.id = "bg-layers";
  host.setAttribute("aria-hidden", "true");
  // Alle Stil-Layer sind immer im DOM; app.css blendet per
  // data-bg-theme genau die passenden ein. display:none-Layer kosten
  // weder Paint noch Composite.
  host.innerHTML =
    '<div class="bgl-blob b1"></div>' +
    '<div class="bgl-blob b2"></div>' +
    '<div class="bgl-blob b3"></div>' +
    '<div class="bgl-wash"></div>' +
    '<div class="bgl-spot"></div>' +
    '<div class="bgl-grid"></div>' +
    '<div class="bgl-grid-spot"></div>' +
    '<div class="bgl-grain"></div>' +
    '<div class="bgl-beam"></div>';
  document.body.insertBefore(host, document.body.firstChild);

  startRasterSpotlight();
}

// Raster-Spotlight: gedaempftes Maus-Following + Sinus-Drift bei
// Inaktivitaet. Schreibt --glow-x/--glow-y auf :root (Defaults stehen
// in app.css, damit vor JS-Start und bei reduced-motion ein statisch
// zentriertes Spotlight sichtbar ist).
function startRasterSpotlight(): void {
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (reducedMotion) return;

  const root = document.documentElement;
  const IDLE_AFTER_MS = 4000;

  let targetX = 50;
  let targetY = 38;
  let x = 50;
  let y = 38;
  let lastMove = 0;
  let rafId = 0;

  if (window.matchMedia("(hover: hover)").matches) {
    window.addEventListener("pointermove", (e) => {
      targetX = (e.clientX / window.innerWidth) * 100;
      targetY = (e.clientY / window.innerHeight) * 100;
      lastMove = performance.now();
    });
  }

  function frame(now: number) {
    rafId = requestAnimationFrame(frame);
    // Idle-Drift nur wenn Bewegung nicht abgeschaltet ist (Regler
    // "Bewegung" in bg-settings); Maus-Following bleibt immer an --
    // das ist Interaktion, keine Ambient-Animation.
    if (now - lastMove > IDLE_AFTER_MS && root.dataset.bgMotion !== "0") {
      targetX = 50 + Math.sin(now / 9000) * 22;
      targetY = 40 + Math.cos(now / 12000) * 14;
    }
    x += (targetX - x) * 0.06;
    y += (targetY - y) * 0.06;
    root.style.setProperty("--glow-x", x.toFixed(2) + "%");
    root.style.setProperty("--glow-y", y.toFixed(2) + "%");
  }

  function stop() {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }

  // Loop nur wenn noetig: Raster aktiv + Tab sichtbar.
  function reconcile() {
    const wantLoop =
      getBgSettings().theme === "raster" && !document.hidden;
    if (wantLoop && rafId === 0) {
      rafId = requestAnimationFrame(frame);
    } else if (!wantLoop && rafId !== 0) {
      stop();
    }
  }

  document.addEventListener("visibilitychange", reconcile);
  subscribeBgSettings(reconcile);
}
